import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

/**
 * A credential that lets its owner call the REST API as themselves, scoped to
 * exactly one zone.
 *
 * __NOTE:__ this is the key as its *owner* sees it. The key itself is never
 * represented here — only a SHA-256 of it is ever persisted, so a key's
 * plaintext exists exactly once, in the response that created it, and cannot
 * be recovered afterwards. Nothing on this type can be used to authenticate
 */
export interface ApiKey {
  /** @see {@link ApiKey.Id} */
  id: ApiKey.Id
  /** Human-readable label so the owner can tell their keys apart */
  name?: string
  /**
   * Whether the key can currently be used. Revoking a key sets this to false
   * rather than deleting it, so the key's usage history is preserved
   */
  enabled: boolean
  /** Epoch millis the key was created */
  createdAt: number
  /** Epoch millis after which the key stops working, if ever */
  expiresAt?: number
  /** Epoch millis of the last request authenticated with this key */
  lastUsedAt?: number
}
export namespace ApiKey {
  /**
   * Identifies an {@link ApiKey} for revocation.
   *
   * This is the key's SHA-256. It is safe to disclose to the key's owner:
   * presenting it as a key would simply hash it a second time, so it cannot be
   * replayed to authenticate
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('ApiKey.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Information needed to create an {@link ApiKey}.
   *
   * Deliberately does not name an owner — a key is always minted for the user
   * making the request
   */
  export interface Input {
    name?: string
    /**
     * Epoch millis after which the key stops working. Omit for a key that
     * never expires
     */
    expiresAt?: number
  }

  /**
   * A newly minted {@link ApiKey}, together with the one and only copy of its
   * plaintext
   */
  export interface Generated {
    /**
     * The plaintext key. Show it to its owner once, then discard it — there is
     * no way to retrieve it again
     */
    key: string
    apiKey: ApiKey
  }
}
